
<script setup>
    import { onMounted, ref, computed, watch } from 'vue'
    import router from '../../router/routes';

    import { useUserStore } from '@/stores/user';
    const userStore = useUserStore();
    
    import { useBusinessPartnersStore } from '@/stores/businessPartners'
    const businessPartnersStore = useBusinessPartnersStore();

    import { useLeadGenerationStore } from '@/stores/leadGeneration'
    const leadGenerationStore = useLeadGenerationStore();

    import { useLeadMatchingStore } from '@/stores/leadMatching'
    const leadMatchingStore = useLeadMatchingStore();
    import { fetchFromBackend } from '@/stores/helpers/backend.mjs';

    async function handleDisplayStatus(display) {
      await userStore.selectDisplayStatus('leadGeneration', display);
    }

    import Bar from '@/components/Bar_new.vue';
    import PreviewHeader from '@/components/PreviewHeader.vue';
    import InlineSpinner from '@/components/InlineSpinner.vue';
    import LoadingOverlay from '@/components/LoadingOverlay.vue';
        // Fullscreen Loading Overlay für Navigation
        const showFullscreenLoading = ref(false);

        async function handleWidgetNavigation(to) {
            showFullscreenLoading.value = true;
            try {
                await router.push(to);
            } finally {
                showFullscreenLoading.value = false;
            }
        }

        function resolveEigenrechercheListId() {
            const lists = userStore.filterOption?.businessPartnerLists || [];
            const exact = lists.find((list) => list?.name === 'Eigenrecherche: Alle');
            const fuzzy = lists.find((list) => /eigenrecherche/i.test(String(list?.name || '')));
            const targetList = exact || fuzzy;
            return targetList?.id ?? null;
        }

        function buildEigenrechercheLeadsRoute() {
            const listId = resolveEigenrechercheListId();
            const baseFilter = JSON.parse(JSON.stringify(userStore.currentFilter.filter || {}));

            if (listId != null) {
                baseFilter.idBusinessPartnerList = listId;
            }

            // Open full Eigenrecherche list without an inherited text search from previous pages.
            baseFilter.search = null;
            baseFilter.idBusinessPartnerStatus = null;
            baseFilter.signature = `EigenrechercheAlle:${baseFilter.idBusinessPartnerList}:${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}`;

            return {
                name: 'BusinessPartnerList',
                query: {
                    source: 'filter',
                    hideFilter: 'true',
                    filter: JSON.stringify(baseFilter),
                },
            };
        }

        const eigenrechercheLeadsRoute = computed(() => buildEigenrechercheLeadsRoute())

        async function handleNavigateToEigenrechercheLeads() {
            showFullscreenLoading.value = true;
            try {
                await router.push(buildEigenrechercheLeadsRoute());
            } finally {
                showFullscreenLoading.value = false;
            }
        }

    // import { onMounted, ref, computed } from 'vue';
    // import { useBusinessPartnersCountStore } from '@/stores/businessPartnersCount';
    // const businessPartnersCountStore = useBusinessPartnersCountStore();
    const MAX_EIGENRECHERCHE = computed(() => userStore.maxReservations ?? 100);
    const eigenrechercheCount = computed(() => leadGenerationStore.eigenrechercheCount);
    watch(eigenrechercheCount, (val) => {
        console.log('PreviewLeadGeneration.vue: eigenrechercheCount changed:', val);
    });
    const isAtCapacity = computed(() => eigenrechercheCount.value >= MAX_EIGENRECHERCHE.value);

    // Fetch and sum businessPartnerCounts for x/100 pill
    // async function fetchEigenrechercheCount() {
    //     await businessPartnersCountStore.getByFilter(
    //         businessPartnersCountStore.namedFilter('eigenrechercheUnbeendetCount')
    //     );
    //     const lists = businessPartnersCountStore.businessPartnerLists.find(l => l.filter && l.filter.signature && l.filter.signature.includes('eigenrechercheUnbeendetCount'));
    //     if (lists && lists.filter && lists.filter.signature && Array.isArray(lists.filter.businessPartnerLists)) {
    //         eigenrechercheCount.value = lists.filter.businessPartnerLists.reduce((sum, item) => sum + (item.businessPartnerCount || 0), 0);
    //     } else if (lists && Array.isArray(lists.businessPartnerLists)) {
    //         eigenrechercheCount.value = lists.businessPartnerLists.reduce((sum, item) => sum + (item.businessPartnerCount || 0), 0);
    //     } else if (lists && typeof lists.count === 'number') {
    //         eigenrechercheCount.value = lists.count;
    //     }
    // }

    onMounted(async () => {
        await leadGenerationStore.fetchEigenrechercheCount();
    });

    // Wenn sich der ausgewählte Sales Agent (oder die Kampagne) ändert, Count neu laden
    const selectionKey = computed(() => {
        const sA = userStore.currentSelection?.salesAgent?.id;
        const team = userStore.currentSelection?.salesTeam?.id;
        const camp = userStore.currentSelection?.campaign?.id;
        return `${sA}:${team}:${camp}`;
    });

    watch(selectionKey, async (newVal, oldVal) => {
        if (newVal === oldVal) return;
        console.log('PreviewLeadGeneration.vue: selectionKey changed, refetch eigenrechercheCount', newVal);
        await leadGenerationStore.fetchEigenrechercheCount();
    });

    // Neues Suchfeld (analog Potential & BK Preview)
    // Query (neue Google Suche) und Filter (lokal auf vorhandene Listen)
    const leadGenerationQuery = ref(''); // erstellt neue Liste(n)
    const leadFilter = ref(''); // filtert vorhandene Listen
    try { const storedFilter = localStorage.getItem('lg_filter'); if(storedFilter) leadFilter.value = storedFilter; } catch(e) {}
    watch(leadFilter,(v)=> { try { localStorage.setItem('lg_filter', v); } catch(e){} });
    const hasLeadSearchContent = ref(false);
    watch(leadGenerationQuery,(v)=> { hasLeadSearchContent.value = v.trim().length>0 });
    const leadMatchingObject = ref({
        id: '-1',
        name: '',
        street: '',
        houseNumber: '',
        postalCode: '',
        city: '',
        url: '',
        emailAddress: '',
        phoneNumber: ''
    });

    const patterns = {
        postalCode: /^\d{4,6}$/,
        url: /^(https?:\/\/)?([\w.-]+)\.([a-z\.]{2,6})([\/\w\-.~:?#[\]@!$&'()*+,;=]*)?$/i,
        phone: /^[+]?([0-9 ]{6,20})$/,
        name: /^.{3,128}$/
    };

    const formErrors = computed(() => {
        const e = [];
        const v = leadMatchingObject.value;
        if (!patterns.name.test(v.name || '')) e.push('Bitte einen gültigen Firmennamen (3–128 Zeichen) angeben.');
        if (!v.street || v.street.trim().length < 2) e.push('Straße ist ein Pflichtfeld.');
        if (!v.houseNumber || v.houseNumber.trim().length < 1) e.push('Hausnummer ist ein Pflichtfeld.');
        if (!v.postalCode || !patterns.postalCode.test(String(v.postalCode))) e.push('Postleitzahl ist ein Pflichtfeld und muss 4–6 Ziffern enthalten.');
        if (!v.city || v.city.trim().length < 2) e.push('Ort ist ein Pflichtfeld.');
        if (v.url && !patterns.url.test(v.url)) e.push('URL prüfen (mit oder ohne https://).');
        if (v.phoneNumber && !patterns.phone.test(String(v.phoneNumber))) e.push('Telefonnummer prüfen (nur Ziffern, Leerzeichen, optional +).');
        return e;
    });
    const isFormValid = computed(() => formErrors.value.length === 0
        && !!leadMatchingObject.value.name
        && !!leadMatchingObject.value.street
        && !!leadMatchingObject.value.houseNumber
        && !!leadMatchingObject.value.postalCode
        && !!leadMatchingObject.value.city
    );

    // Neue Suche starten (Limit 4 parallele Recherchen)
    // Recent Searches (analog Potential & BK) for Eigenrecherche (store queries for quick re-run)
    const LG_MAX_RECENT = 8;
    const recentLeadQueries = ref([]); // array of raw query strings (lowercase normalized display later capitalized)
    try {
        const rawLg = localStorage.getItem('lg_recentSearches');
        if(rawLg){
            const arr = JSON.parse(rawLg);
            if(Array.isArray(arr)) recentLeadQueries.value = arr.slice(0, LG_MAX_RECENT);
        }
    } catch(e){ console.warn('Failed to parse lg_recentSearches', e); }
    function persistRecentLead(){ try { localStorage.setItem('lg_recentSearches', JSON.stringify(recentLeadQueries.value)); } catch(e){} }
    function addRecentLeadQuery(q){
        const norm = q.trim();
        if(!norm) return;
        const idx = recentLeadQueries.value.findIndex(s => s.toLowerCase() === norm.toLowerCase());
        if(idx!==-1){
            const [it]=recentLeadQueries.value.splice(idx,1);
            recentLeadQueries.value.unshift(it);
        } else {
            recentLeadQueries.value.unshift(norm);
            if(recentLeadQueries.value.length>LG_MAX_RECENT) recentLeadQueries.value.pop();
        }
        persistRecentLead();
    }
    function removeRecentLeadQuery(q){
        const idx = recentLeadQueries.value.findIndex(s=>s===q);
        if(idx!==-1){ recentLeadQueries.value.splice(idx,1); persistRecentLead(); }
    }
    function clearRecentLeadQueries(){ recentLeadQueries.value = []; try { localStorage.removeItem('lg_recentSearches'); } catch(e){} }

    const MAX_CONCURRENT = 3;
    const isCheckingMatchLeads = ref(false);
    const retryingEmptySignature = ref('');
    const matchLeadsError = ref('');
    const handleLeadGeneration = async () => {
        const query = leadGenerationQuery.value.trim();
        if(!query) return;
        if(leadGenerationStore.leadLists.length >= MAX_CONCURRENT) return;
        addRecentLeadQuery(query);
        leadGenerationQuery.value = '';
        matchLeadsError.value = '';
        leadGenerationStore.matchLeadsIsAvailable = null;
        try {
            await leadGenerationStore.checkMatchLeadsIsAvailable();
            if (!leadGenerationStore.matchLeadsIsAvailable) {
                matchLeadsError.value = 'Matching Service ist aktuell nicht erreichbar. Bitte später erneut versuchen.';
                return;
            }
            await leadGenerationStore.processQuery(query.toLocaleLowerCase());
        } catch (e) {
            console.error(e)
			matchLeadsError.value = 'Fehler beim Prüfen des Matching Service. Bitte später erneut versuchen.';
        } finally {

        }
    };
    function onLeadSearchKey(e){ if(e.key==='Enter'){ e.preventDefault(); handleLeadGeneration(); }}

    async function handleRetryEmptyLeadList(signature) {
        if (!signature) return;
        if (retryingEmptySignature.value === signature) return;

        const raw = signature.replace(/^googlePlacesSearch:/, '');
        const decoded = decodeURIComponent(raw).trim();
        if (!decoded) return;

        retryingEmptySignature.value = signature;
        matchLeadsError.value = '';
        leadGenerationStore.matchLeadsIsAvailable = null;

        try {
            await leadGenerationStore.checkMatchLeadsIsAvailable();
            if (!leadGenerationStore.matchLeadsIsAvailable) {
                matchLeadsError.value = 'Matching Service ist aktuell nicht erreichbar. Bitte später erneut versuchen.';
                return;
            }

            await leadGenerationStore.removeQuery(signature);
            await leadGenerationStore.processQuery(decoded.toLocaleLowerCase());
            addRecentLeadQuery(decoded);
            addRecentlyViewed(`googlePlacesSearch:${encodeURIComponent(decoded.toLocaleLowerCase())}`);
        } catch (e) {
            matchLeadsError.value = 'Fehler beim erneuten Starten der Suche. Bitte später erneut versuchen.';
        } finally {
            retryingEmptySignature.value = '';
        }
    }

    const handleLeadMatching = async () => {
        if (!isFormValid.value) return;
        if (userStore.currentSelection.leadMatching.isLoading) return;
        // Store recent match before routing
        addRecentCompanyMatch(leadMatchingObject.value);
        // Ensure id: '-1' is always sent as string (API expects string)
        const leadWithId = { ...leadMatchingObject.value, id: '-1' };
        // Patch: force id: '-1' in businessPartner for API request
        
		// zuerst wird geprüft, ob der Matching Service erreichbar ist (z.B. könnte Backend Problem vorliegen).
		// Zeige Loading-Indicator in UI und setze vorherige Fehler zurück
		leadMatchingStore.matchLeadsIsAvailable = null;
		matchLeadsError.value = '';
		try {
			await leadMatchingStore.checkMatchLeadsIsAvailable();
			if (!leadMatchingStore.matchLeadsIsAvailable) {
				matchLeadsError.value = 'Matching Service ist aktuell nicht erreichbar. Bitte später erneut versuchen.';
				return;
			}

			await leadMatchingStore.matchLeadAgainstBusinessPartnerIndex({ ...leadWithId });
			leadMatchingStore.setLastFormLead({ ...leadWithId });
			router.push({ name: 'LeadMatching', query: { signature: leadWithId.name } });
			// Reset after processing
			leadMatchingObject.value = {
				id: '-1', name: '', street: '', houseNumber: '', postalCode: '', city: '', url: '', emailAddress: '', phoneNumber: ''
			};
		} catch (e) {
			console.error('Error checking matching availability or matching lead', e);
			matchLeadsError.value = 'Fehler beim Prüfen des Matching Service. Bitte später erneut versuchen.';
			return;
		}
    };

    const ocrLoading = ref(false);
    const ocrError = ref('');
    const ocrText = ref('');

    function fileToDataUrl(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden.'));
            reader.readAsDataURL(file);
        });
    }

    async function resizeImageDataUrl(dataUrl, maxWidth = 600, quality = 0.82) {
        const img = new Image();
        const loaded = new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = () => reject(new Error('Bild konnte nicht geladen werden.'));
        });
        img.src = dataUrl;
        await loaded;

        const ratio = img.width > maxWidth ? (maxWidth / img.width) : 1;
        const width = Math.max(1, Math.round(img.width * ratio));
        const height = Math.max(1, Math.round(img.height * ratio));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return dataUrl;
        ctx.drawImage(img, 0, 0, width, height);
        return canvas.toDataURL('image/jpeg', quality);
    }

    function applyOcrTextToLeadForm(text) {
        if (!text || typeof text !== 'string') return;
        const lines = text
            .split(/\r?\n/)
            .map(l => l.trim())
            .filter(Boolean);

        const byRegex = (regex) => {
            const match = text.match(regex);
            return match ? match[0].trim() : '';
        };

        const email = byRegex(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
        const url = byRegex(/(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)+[^\s]*/i);
        const phone = byRegex(/(?:\+\d{1,3}[\s\/-]?)?(?:\(?\d{2,5}\)?[\s\/-]?){2,5}\d{2,}/);

        const postalCityLine = lines.find(l => /\b\d{5}\b/.test(l));
        let postalCode = '';
        let city = '';
        if (postalCityLine) {
            const pcMatch = postalCityLine.match(/\b(\d{5})\b\s+(.+)/);
            if (pcMatch) {
                postalCode = pcMatch[1] || '';
                city = (pcMatch[2] || '').trim();
            }
        }

        let street = '';
        let houseNumber = '';
        const streetLine = lines.find(l => /\d+[a-zA-Z]?$/.test(l) && !/\b\d{5}\b/.test(l));
        if (streetLine) {
            const streetMatch = streetLine.match(/^(.*?)[,\s]+(\d+[a-zA-Z]?)$/);
            if (streetMatch) {
                street = (streetMatch[1] || '').trim();
                houseNumber = (streetMatch[2] || '').trim();
            }
        }

        const candidateName = lines.find(l => {
            const lower = l.toLowerCase();
            if (email && l.includes(email)) return false;
            if (url && l.includes(url)) return false;
            if (phone && l.includes(phone)) return false;
            if (/\b\d{5}\b/.test(l)) return false;
            if (lower.includes('tel') || lower.includes('fax') || lower.includes('www') || lower.includes('@')) return false;
            return l.length >= 3;
        }) || '';

        if (!leadMatchingObject.value.name && candidateName) leadMatchingObject.value.name = candidateName;
        if (!leadMatchingObject.value.street && street) leadMatchingObject.value.street = street;
        if (!leadMatchingObject.value.houseNumber && houseNumber) leadMatchingObject.value.houseNumber = houseNumber;
        if (!leadMatchingObject.value.postalCode && postalCode) leadMatchingObject.value.postalCode = postalCode;
        if (!leadMatchingObject.value.city && city) leadMatchingObject.value.city = city;
        if (!leadMatchingObject.value.phoneNumber && phone) leadMatchingObject.value.phoneNumber = phone;
        if (!leadMatchingObject.value.url && url) {
            leadMatchingObject.value.url = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        }
        if (!leadMatchingObject.value.emailAddress && email) leadMatchingObject.value.emailAddress = email;
    }

    const handleOcrFileSelected = async (event) => {
        const input = event?.target;
        const file = input?.files?.[0];
        if (!file) return;

        ocrError.value = '';
        ocrText.value = '';
        ocrLoading.value = true;
        try {
            const dataUrl = await fileToDataUrl(file);
            const resizedDataUrl = await resizeImageDataUrl(dataUrl, 600, 0.82);
            const result = await fetchFromBackend('ocr/v1/scanDocument', { base64Image: resizedDataUrl });

            if (result?.code) {
                throw new Error(result?.message || 'OCR-Scan fehlgeschlagen.');
            }

            if (!result || typeof result.text !== 'string') {
                throw new Error('Keine OCR-Daten erhalten.');
            }

            ocrText.value = result.text;
            applyOcrTextToLeadForm(result.text);
        } catch (err) {
            ocrError.value = err?.message || 'OCR-Scan fehlgeschlagen.';
        } finally {
            ocrLoading.value = false;
            if (input) input.value = '';
        }
    };

    const handleSelectLeadList = (signature) => {
        console.log('routing to lead:', signature);
        addRecentlyViewed(signature);
        router.push({ name: 'LeadGeneration', query: { signature: signature}});
    };

    function capitalize(str) {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    function formatSignature(signature) {
        if (!signature) return '';
        const withoutPrefix = signature.replace('googlePlacesSearch:', '');
        const decoded = decodeURIComponent(withoutPrefix);
        return capitalize(decoded);
    }

    function getProcessedCount(data) {
        if (!data || !Array.isArray(data)) return 0;
        return data.filter(item => item.processed).length;
    }

    function isLeadListEmpty(list) {
        return !list?.isLoading && Array.isArray(list?.data) && list.data.length === 0;
    }

    function getLowConfidenceAndFarDistanceCount(data) {
        if (!data || !Array.isArray(data)) return 0;
        return data.filter(item => item.match?.confidence < 0.2 && item.match?.distance > 3).length;
    }

    const hasNoResults = computed(() => leadGenerationStore.leadLists.length === 0);

    // Bubble Klassifikation (ähnlich Potential & BK) nach Größe
    function bubbleClassLead(list){
        const len = Array.isArray(list.data)? list.data.length : 0;
        if(len >= 120) return 'high';
        if(len >= 60) return 'mid';
        return 'base';
    }

    // (Mini-Stats entfernt)

    function handleRemoveLead(signature) {
        leadGenerationStore.removeQuery(signature);
    }

    // Recently viewed lead lists (local, max 3)
    const MAX_RECENT = 6; // konfigurierbar
    const recentlyViewed = ref([]); // array of signatures
    // load from localStorage
    try { const stored = JSON.parse(localStorage.getItem('lg_recentlyViewed')); if(Array.isArray(stored)) recentlyViewed.value = stored.slice(0,MAX_RECENT); } catch(e) {}
    function persistRecentlyViewed(){ localStorage.setItem('lg_recentlyViewed', JSON.stringify(recentlyViewed.value)); }
    function addRecentlyViewed(signature){
        if(!signature) return;
        const idx = recentlyViewed.value.indexOf(signature);
        if(idx!==-1) recentlyViewed.value.splice(idx,1); // move to front
        recentlyViewed.value.unshift(signature);
        if(recentlyViewed.value.length>MAX_RECENT) recentlyViewed.value.splice(MAX_RECENT);
        persistRecentlyViewed();
    }
    function removeRecentlyViewed(signature){
        const idx = recentlyViewed.value.indexOf(signature);
        if(idx!==-1){
            recentlyViewed.value.splice(idx,1);
            persistRecentlyViewed();
        }
    }
    const activeLeadSignature = computed(()=> leadGenerationStore.currentLead?.signature || leadGenerationStore.leadLists.find(l=> l.signature === (router.currentRoute.value.query.signature))?.signature );
    const recentHeadline = computed(()=> {
        if(!recentlyViewed.value.length) return 'Letzte Suchen';
        return recentlyViewed.value.length===1 ? 'Letzte Suche' : `Letzte Suchen (${recentlyViewed.value.length})`;
    });

    // Klick auf eine "Letzte Suchen"-Pill:
    //  - Falls Liste (signature) noch im Store existiert => normale Navigation
    //  - Falls nicht mehr vorhanden => Query erneut starten (neue Suche anstoßen)
    const handleRecentClick = async (signature) => {
        if(!signature) return;
        const existing = leadGenerationStore.leadLists.find(l => l.signature === signature);
        if(existing){
            handleSelectLeadList(signature);
            return;
        }
        // Neue Suche nur starten, wenn Slots frei
    if(leadGenerationStore.leadLists.length >= MAX_CONCURRENT) return;
        // Query aus Signatur extrahieren
        const raw = signature.replace(/^googlePlacesSearch:/,'');
        const decoded = decodeURIComponent(raw);
        if(!decoded.trim()) return;
        await leadGenerationStore.processQuery(decoded.toLocaleLowerCase());
        addRecentLeadQuery(decoded);
        // Nach erneuter Suche wieder in Recently Viewed nach vorne setzen
        addRecentlyViewed(`googlePlacesSearch:${encodeURIComponent(decoded.toLocaleLowerCase())}`);
    };

    // Gefilterte Listen für Anzeige (inline + preview) anhand leadFilter
    const filteredLeadLists = computed(()=> {
        const term = leadFilter.value.trim().toLowerCase();
        if(!term) return leadGenerationStore.leadLists;
        return leadGenerationStore.leadLists.filter(l=> formatSignature(l.signature).toLowerCase().includes(term));
    });

    // UI: Toggle between query history and recently opened lists
    const recentTab = ref('queries'); // 'queries' | 'lists'

    // Letzte Abgleiche (Formular-Historie) für Bestandsabgleich
    const LM_MAX_RECENT = 8;
    const recentCompanyMatches = ref([]); // array of minimal lead objects
    function matchSignature(lead){
        const pc = (lead.postalCode||'').toString().trim();
        return [lead.name||'', pc, lead.city||''].join('|').toLowerCase();
    }
    try {
        const rawLm = localStorage.getItem('lm_recentMatches');
        if(rawLm){
            const arr = JSON.parse(rawLm);
            if(Array.isArray(arr)) recentCompanyMatches.value = arr.slice(0, LM_MAX_RECENT);
        }
    } catch(e) { console.warn('Failed to parse lm_recentMatches', e); }
    function persistRecentMatches(){ try { localStorage.setItem('lm_recentMatches', JSON.stringify(recentCompanyMatches.value)); } catch(e){} }
    function addRecentCompanyMatch(lead){
        const compact = {
            name: lead.name||'',
            street: lead.street||'',
            houseNumber: lead.houseNumber||'',
            postalCode: lead.postalCode||'',
            city: lead.city||'',
            url: lead.url||'',
            phoneNumber: lead.phoneNumber||''
        };
        const sig = matchSignature(compact);
        const idx = recentCompanyMatches.value.findIndex(m => matchSignature(m) === sig);
        if(idx!==-1){
            const [it]=recentCompanyMatches.value.splice(idx,1);
            recentCompanyMatches.value.unshift(it);
        } else {
            recentCompanyMatches.value.unshift(compact);
            if(recentCompanyMatches.value.length>LM_MAX_RECENT) recentCompanyMatches.value.pop();
        }
        persistRecentMatches();
    }
    function removeRecentCompanyMatch(m){
        const sig = matchSignature(m);
        const idx = recentCompanyMatches.value.findIndex(x => matchSignature(x) === sig);
        if(idx!==-1){ recentCompanyMatches.value.splice(idx,1); persistRecentMatches(); }
    }
    function clearRecentCompanyMatches(){ recentCompanyMatches.value = []; try { localStorage.removeItem('lm_recentMatches'); } catch(e){} }
    function handleRecentCompanyClick(m){
        // Prefill form only; let user explicitly submit
        leadMatchingObject.value = { ...m };
    }
</script>

<template>
        <div class="leadGeneration-preview-block leadGeneration">
            <preview-header title="Eigenrecherche"
                :to="eigenrechercheLeadsRoute"
                button-label="Zur Leads Liste"
                :disabled="false"
                @click="handleNavigateToEigenrechercheLeads"
            />
    <div id="lead-generation" :class="['widget-wrapper lead-generation']">
        <LoadingOverlay :show="showFullscreenLoading" label="Daten werden geladen…" spinner-size="lg" />
        <!-- Neue Suchsektion -->
        <div v-if="userStore.currentSelection.leadGeneration.display==='google'" class="container-wrapper search-below-wrapper">
            <div class="container main-content">
                <div class="capacity-inline" :class="{'at-limit': isAtCapacity}">
                    <span class="cap-label">Eigenrecherche</span>
                    <span class="cap-counter-pills">
                    <span class="cap-pill" :class="{'cap-pill--over': filteredLeadLists.length >= MAX_CONCURRENT}" title="Parallele Suchen">
                        {{ filteredLeadLists.length }} / {{ MAX_CONCURRENT }}
                    </span>
                    <span class="cap-pill" :class="{'cap-pill--over': eigenrechercheCount > MAX_EIGENRECHERCHE}" title="Gesamtkapazität">
                        <template v-if="leadGenerationStore.isLoadingEigenrechercheCount">
                            <InlineSpinner size="sm" />
                        </template>
                        <template v-else>
                            {{ eigenrechercheCount }}
                        </template>
                        / {{ MAX_EIGENRECHERCHE }}
                    </span>
                    </span>
                    <span v-if="isAtCapacity" class="cap-msg">Kapazitätslimit erreicht</span>
                </div>
                <div v-if="!isAtCapacity" class="search-block below">
                    <input v-model="leadGenerationQuery" @keydown="onLeadSearchKey" type="text" placeholder="Google Suche: z.B. Maler Krefeld" class="search-input" autocomplete="off" :disabled="filteredLeadLists.length >= MAX_CONCURRENT || leadGenerationStore.isLoadingEigenrechercheCount || isCheckingMatchLeads" />
                    <button class="btn btn--pill btn--sm btn--solid search-btn" @click="handleLeadGeneration" :disabled="!leadGenerationQuery || filteredLeadLists.length >= MAX_CONCURRENT || leadGenerationStore.isLoadingEigenrechercheCount || isCheckingMatchLeads" title="Suchen">
                        <img src="@/assets/imgs/icons/magnifying-glass-solid.svg" alt="" aria-hidden="true" />
                        <span class="sr-only">Suchen</span>
                    </button>
                    <div v-if="leadGenerationStore.matchLeadsIsAvailable === null" class="form-errors search-loading"><InlineSpinner size="sm" />&nbsp;Matching Service wird geprüft ...</div>
                    <div v-if="filteredLeadLists.length >= MAX_CONCURRENT" class="form-errors search-limit-error">Maximale Anzahl paralleler Suchen erreicht.</div>
                </div>
                <div v-else class="form-errors search-limit-error">Du hast das Kapazitätslimit erreicht. Bitte bearbeite zuerst bestehende Eigenrecherche-Neukunden, um wieder Kapazität zu bekommen.</div>
                <div v-if="matchLeadsError" class="form-errors search-error">{{ matchLeadsError }}</div>
            </div>
        </div>
        <!-- (Mini-Stats und Inline Leadlists entfernt) -->
        <!-- Kompakte Vorschau der ersten zwei Listen -->
        <div class="container-wrapper">
            <div v-if="userStore.currentSelection.leadGeneration.display==='google'" class="container main-content">
                <div class="leadListWrapper">
                    <div
                        v-for="filteredLeadList of filteredLeadLists.slice(0,3)"
                        :key="filteredLeadList.signature || filteredLeadList.id"
                        class="leadList"
                        :class="{ 'is-loading': filteredLeadList.isLoading, 'is-empty': isLeadListEmpty(filteredLeadList) }"
                        @click="!filteredLeadList.isLoading && !isLeadListEmpty(filteredLeadList) && handleSelectLeadList(filteredLeadList.signature)"
                    >
                    <div v-if="filteredLeadList.isLoading" class="loadingSpinner cell-grid container"><InlineSpinner size="sm" label="Google Suche läuft..." />Google suche läuft ...</div>
                        <div v-else class="leadListContent">
                            <template v-if="isLeadListEmpty(filteredLeadList)">
                                <div class="leadListEmpty">
                                    <h6>{{ formatSignature(filteredLeadList.signature) }}</h6>
                                    <p>Keine Treffer fuer diese Suche</p>
                                    <button
                                        type="button"
                                        class="btn btn--pill btn--sm btn--solid empty-retry-btn"
                                        @click.stop="handleRetryEmptyLeadList(filteredLeadList.signature)"
                                        :disabled="retryingEmptySignature === filteredLeadList.signature"
                                    >
                                        {{ retryingEmptySignature === filteredLeadList.signature ? 'Suche startet...' : 'Erneut suchen' }}
                                    </button>
                                </div>
                            </template>
                            <template v-else>
                                <div class="leadListBar">
                                    <div class="headlineWrapper">
                                        <div class="sideLeft">
                                            <h6>{{formatSignature(filteredLeadList.signature)}}</h6>
                                            <template v-if="getProcessedCount(filteredLeadList.data) < filteredLeadList.data.length"><p>|</p><span>Lädt</span><span class="loaderPoints"></span></template>
                                        </div>
                                        <div class="sideRight"><span>Zur Leads Liste</span></div>
                                    </div>
                                    <div class="bars-wrapper">
                                        <bar :class="'leadGeneration'" :mode="'leadGeneration'" :title="`verglichen`" :currentValue="getProcessedCount(filteredLeadList.data)" :goalValue="filteredLeadList.data.length" />
                                    </div>
                                </div>
                            </template>
                            <button class="delete" @click.stop="handleRemoveLead(filteredLeadList.signature)"><img src="@/assets/imgs/icons/trash-can-solid.svg" alt="Delete Icon" /></button>
                        </div>
                    </div>
                    <template v-if="filteredLeadLists.length < MAX_CONCURRENT">
                        <div v-for="i in (MAX_CONCURRENT - filteredLeadLists.length)" :key="'dummy-'+i" class="leadList dummy cell-grid">
                            <h6>Bitte eine Suche starten...</h6>
                            <div class="bars-wrapper"><bar :class="'leadGeneration'" :mode="'leadGeneration'" :title="''" :currentValue="0" :goalValue="0" /></div>
                        </div>
                    </template>
                    <!-- removed more-hint -->
                </div>
            </div>
        </div>
        <!-- Letzte Suchen (ehemals zwei Blöcke: latest-searches + recently-viewed zusammengeführt) -->
        <div v-if="userStore.currentSelection.leadGeneration.display==='google'" class="container-wrapper latest-searches-wrapper">
            <div class="container main-content" style="position:relative;">
                <div :class="['recently-viewed latest-searches', {'cell-grid' : isAtCapacity }]">
                    <div class="tabs-switch" role="tablist">
                        <button :class="['tab-btn',{active:recentTab==='queries'}]" role="tab" :aria-selected="recentTab==='queries'" @click="recentTab='queries'">Suchbegriffe</button>
                        <button :class="['tab-btn',{active:recentTab==='lists'}]" role="tab" :aria-selected="recentTab==='lists'" @click="recentTab='lists'">Geöffnete Listen</button>
                    </div>
                    <div v-if="recentTab==='queries'" class="tab-panel" role="tabpanel">
                        <h6 class="rv-title">Letzte Suchbegriffe</h6>
                        <div v-if="recentLeadQueries.length" class="recent-searches-wrapper">
                            <div class="rs-headline-row inline-actions">
                                <button type="button" class="rs-clear" @click="clearRecentLeadQueries" title="Alle löschen" aria-label="Alle löschen" v-if="recentLeadQueries.length>1">Alle löschen</button>
                            </div>
                            <ul class="recent-searches" role="list">
                                <li v-for="q in recentLeadQueries" :key="q" role="listitem"
                                    :class="{ 'pill-disabled': isAtCapacity }"
                                    :style="isAtCapacity ? 'background:#f8d7da; color:#842029; border:1px solid #f5c2c7; cursor:not-allowed;' : ''"
                                    :title="isAtCapacity ? 'Kapazitätslimit erreicht – Suchbegriff gesperrt' : q">
                                    <button v-if="!isAtCapacity" type="button" class="rs-item-btn" @click="leadGenerationQuery = q; handleLeadGeneration();">
                                        <span class="lbl ellipsis" :title="q">{{ q }}</span>
                                    </button>
                                    <span v-else class="lbl ellipsis">{{ q }}</span>
                                    <button v-if="!isAtCapacity" type="button" class="rs-remove" @click="removeRecentLeadQuery(q)" aria-label="Entfernen" title="Entfernen">
                                        <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M18.3 5.71L12 12.01l-6.3-6.3-1.42 1.42 6.3 6.29-6.3 6.3 1.42 1.42 6.29-6.3 6.3 6.3 1.42-1.42-6.3-6.3 6.3-6.29z"/></svg>
                                    </button>
                                </li>
                            </ul>
                            <ul class="recent-searches" role="list">
                                <li v-for="sig in recentlyViewed" :key="sig" :class="['mini-pill', { 'is-active': activeLeadSignature===sig }]" @click="handleRecentClick(sig)" :title="formatSignature(sig)">
                                    <span class="mini-pill-label">{{ formatSignature(sig) }}</span>
                                    <span class="mini-pill-number" v-if="leadGenerationStore.leadLists.find(l=>l.signature===sig)" :class="bubbleClassLead(leadGenerationStore.leadLists.find(l=>l.signature===sig))">
                                        {{ getProcessedCount(leadGenerationStore.leadLists.find(l=>l.signature===sig)?.data) }}/{{ leadGenerationStore.leadLists.find(l=>l.signature===sig)?.data?.length || 0 }}
                                    </span>
                                    <button class="mini-pill-remove" type="button" @click.stop="removeRecentlyViewed(sig)" aria-label="Entfernen">×</button>
                                </li>
                            </ul>
                        </div>
                        <p v-else class="rv-empty">Noch keine Suchbegriffe.</p>
                    </div>
                    <div v-else class="tab-panel" role="tabpanel">
                        <h6 class="rv-title">{{ recentHeadline }}</h6>
                        <template v-if="recentlyViewed.length">
                            <ul class="rv-list">
                                <li v-for="sig in recentlyViewed" :key="sig" :class="{'is-active': activeLeadSignature===sig}" @click="handleRecentClick(sig)" :title="formatSignature(sig)">
                                    <span class="lbl ellipsis">{{ formatSignature(sig) }}</span>
                                    <span class="number-label xs" v-if="leadGenerationStore.leadLists.find(l=>l.signature===sig)" :class="bubbleClassLead(leadGenerationStore.leadLists.find(l=>l.signature===sig))">
                                        {{ getProcessedCount(leadGenerationStore.leadLists.find(l=>l.signature===sig)?.data) }}/{{ leadGenerationStore.leadLists.find(l=>l.signature===sig)?.data?.length || 0 }}
                                    </span>
                                    <button class="rv-remove" type="button" @click.stop="removeRecentlyViewed(sig)" aria-label="Entfernen">×</button>
                                </li>
                            </ul>
                        </template>
                        <p v-else class="rv-empty">Noch keine Liste geöffnet.</p>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="userStore.currentSelection.leadGeneration.display==='company'" class="container main-content matching-form-container">
            <div v-if="userStore.currentSelection.leadMatching.isLoading" class="matching-loading-overlay">
                <InlineSpinner size="sm" label="Matching läuft..." />
                <span>Matching läuft ...</span>
            </div>
            <!-- <div class="ocr-upload-card">
                <div class="ocr-upload-head">
                    <h6>Visitenkarte oder Türschild scannen</h6>
                    <label class="ocr-upload-btn" for="ocr-upload-input">Bild hochladen / Kamera</label>
                    <input id="ocr-upload-input" class="ocr-upload-input" type="file" accept="image/*" capture="environment" @change="handleOcrFileSelected" />
                </div>
                <div v-if="ocrLoading" class="ocr-status"><InlineSpinner size="sm" /> Text wird ausgelesen ...</div>
                <div v-else-if="ocrError" class="form-errors ocr-error">{{ ocrError }}</div>
                <p v-if="ocrText" class="ocr-hint">Felder wurden, soweit erkennbar, automatisch vorausgefüllt.</p>
            </div> -->
            <form class="form-card" @submit.prevent="handleLeadMatching" novalidate>
                <fieldset>
                    <legend class="form-title">Bestandsabgleich</legend>
                    <p class="form-sub">Bitte Firmendaten eingeben. <span class="required">Alle Felder mit * sind Pflichtfelder.</span></p>
                    <div class="form-grid-lead-matching">
                        <div class="login-field full-width">
                            <label for="lg-name" class="sr-only">Firmenname*</label>
                            <img src="@/assets/imgs/icons/user-solid.svg" alt="" aria-hidden="true">
                            <input id="lg-name" class="login-input" type="text" v-model.trim="leadMatchingObject.name" placeholder="Firmenname*" :aria-invalid="!isFormValid && !leadMatchingObject.name" required>
                        </div>
                        <div class="login-field street">
                            <label for="lg-street" class="sr-only">Straße*</label>
                            <img src="@/assets/imgs/icons/location-dot-solid.svg" alt="" aria-hidden="true">
                            <input id="lg-street" class="login-input" type="text" v-model.trim="leadMatchingObject.street" placeholder="Straße*" :aria-invalid="!isFormValid && !leadMatchingObject.street" required>
                        </div>
                        <div class="login-field house-number">
                            <label for="lg-house" class="sr-only">Hausnummer*</label>
                            <img src="@/assets/imgs/icons/location-dot-solid.svg" alt="" aria-hidden="true">
                            <input id="lg-house" class="login-input" type="text" v-model.trim="leadMatchingObject.houseNumber" placeholder="Hausnummer*" :aria-invalid="!isFormValid && !leadMatchingObject.houseNumber" required>
                        </div>
                        <div class="login-field postal-code">
                            <label for="lg-postal" class="sr-only">Postleitzahl*</label>
                            <img src="@/assets/imgs/icons/location-dot-solid.svg" alt="" aria-hidden="true">
                            <input id="lg-postal" class="login-input" type="text" v-model.trim="leadMatchingObject.postalCode" placeholder="Postleitzahl*" inputmode="numeric" :aria-invalid="!isFormValid && !leadMatchingObject.postalCode" required>
                        </div>
                        <div class="login-field city">
                            <label for="lg-city" class="sr-only">Ort*</label>
                            <img src="@/assets/imgs/icons/location-dot-solid.svg" alt="" aria-hidden="true">
                            <input id="lg-city" class="login-input" type="text" v-model.trim="leadMatchingObject.city" placeholder="Ort*" :aria-invalid="!isFormValid && !leadMatchingObject.city" required>
                        </div>
                        <div class="login-field full-width">
                            <label for="lg-url" class="sr-only">Website</label>
                            <img src="@/assets/imgs/icons/earth-europe-solid.svg" alt="" aria-hidden="true">
                            <input id="lg-url" class="login-input" type="url" v-model.trim="leadMatchingObject.url" placeholder="Website (optional)">
                        </div>
                        <div class="login-field full-width">
                            <label for="lg-phone" class="sr-only">Telefonnummer</label>
                            <img src="@/assets/imgs/icons/phone-solid.svg" alt="" aria-hidden="true">
                            <input id="lg-phone" class="login-input" type="tel" v-model.trim="leadMatchingObject.phoneNumber" placeholder="Telefon (optional)">
                        </div>
                    </div>
                    <ul v-if="formErrors.length" class="form-errors" role="alert">
                        <li v-for="(err,i) in formErrors" :key="i">{{ err }}</li>
                    </ul>
                    <div class="actions">
                        <button type="submit" class="btn btn--solid" :disabled="!isFormValid || userStore.currentSelection.leadMatching.isLoading">Absenden</button>
                    </div>
                </fieldset>
            </form>
            <div v-if="leadMatchingStore.matchLeadsIsAvailable === null" class="form-errors search-loading"><InlineSpinner size="sm" />&nbsp;Matching Service wird geprüft ...</div>
            <div v-if="matchLeadsError" class="form-errors search-error">{{ matchLeadsError }}</div>
        </div>
        <!-- Letzte Abgleiche: eigener Container wie bei Google-Suche -->
        <div v-if="userStore.currentSelection.leadGeneration.display==='company'" :class="['container-wrapper latest-searches-wrapper', {'cell-grid' : isAtCapacity }]">
            <div class="container main-content">
                <div class="recently-viewed latest-searches">
                    <h6 class="rv-title">Letzte Abgleiche</h6>
                    <div v-if="recentCompanyMatches.length" class="recent-searches-wrapper">
                        <div class="rs-headline-row inline-actions">
                            <button type="button" class="rs-clear" @click="clearRecentCompanyMatches" title="Alle löschen" aria-label="Alle löschen" v-if="recentCompanyMatches.length>1">Alle löschen</button>
                        </div>
                        <ul class="recent-searches" role="list">
                            <li v-for="m in recentCompanyMatches" :key="matchSignature(m)" role="listitem">
                                <button type="button" class="rs-item-btn" @click="handleRecentCompanyClick(m)">
                                    <span class="lbl ellipsis" :title="m.name + (m.city? ' · '+m.city : '')">{{ m.name }}<template v-if="m.city"> · {{ m.city }}</template></span>
                                </button>
                                <button type="button" class="mini-pill-remove" @click="removeRecentCompanyMatch(m)" aria-label="Entfernen" title="Entfernen">
                                    <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M18.3 5.71L12 12.01l-6.3-6.3-1.42 1.42 6.3 6.29-6.3 6.3 1.42 1.42 6.29-6.3 6.3 6.3 1.42-1.42-6.3-6.3 6.3-6.29z"/></svg>
                                </button>
                            </li>
                        </ul>
                    </div>
                    <p v-else class="rv-empty">Noch keine Abgleiche.</p>
                </div>
            </div>
        </div>
        <div class='wave-wrapper'>
            <div class='wave -one'></div>
            <div class='wave -two'></div>
            <div class='wave -three'></div>
        </div>
        <div class="toggle-wrapper">
            <div class="toggle-option" @click="handleDisplayStatus('google')">
                <span class="toggle-label">vs Google</span>
                <label class="switch">
                    <input type="radio" name="leadGeneration-toggle" value="google" :checked="userStore.currentSelection.leadGeneration.display==='google'">
                    <span class="custom-radio"></span>
                </label>
            </div>
            <div class="toggle-option" @click="handleDisplayStatus('company')" >
                <span class="toggle-label">vs Bestand</span>
                <label class="switch">
                    <input type="radio" name="leadGeneration-toggle" value="company" :checked="userStore.currentSelection.leadGeneration.display==='company'">
                    <span class="custom-radio"></span>
                </label>
            </div>
        </div>
    </div>
</div>
</template>

<style lang="scss" scoped>
$color_1: #842029;
$color_2: #425066;
$color_3: #1677c5;
$color_4: #8ca0b3;
$color_5: #b0b8c1;
$color_6: #334155;
$color_7: #475569;
$color_8: #e53935;
$color_9: #9aa8b8;
$color_10: var(--sellwerk-color-light, #fff);
$color_11: var(--sellwerk-color-dark, #030303);
$color_12: #64748b;
$color_13: #8fa2b2;
$color_14: #f59e0b;
$color_15: #dbe5ec;
$color_16: #fff;
$color_17: #556575;
$color_18: #1e293b;
$color_19: #8a5a14;
$color_20: #e2e8f0;
$color_21: inherit;
$color_22: #94a3b8;
$color_23: #cbd5e1;
$color_24: #7c8a96;
$color_25: #d5dae3;
$color_26: #8896a3;
$color_27: #111;
$color_28: #fecaca;
$color_29: #7f1d1d;
$border-color_1: #a7cbe7;
$border-color_2: var(--sellwerk-color-tertiary, #28817e);
$border-color_3: var(--sellwerk-color-secondary, #97d2d4);
$border-color_4: var(--sellwerk-color-main, #00a393);
$border-color_5: #334155;
$border-color_6: #364351;
$border-color_7: color-mix(in srgb,var(--sellwerk-color-secondary,#1790da) 55%, #2c3742);
$border-color_8: #d6e0e7;
$border-color_9: #c9d3db;
$border-color_10: color-mix(in srgb,var(--sellwerk-color-main,#1677c5) 55%, #c9d3db);
$border-color_11: #d3dbe3;
$border-color_12: #d5dde5;
$border-color_13: #d3dde5;
$border-color_14: #fda4af;
$border-color_15: var(--sellwerk-color-tertiary,#05635c);


@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateY(4px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.cap-pill--over {
	background: #f8d7da !important;
	color: $color_1 !important;
	border: 1px solid #f5c2c7 !important;
	box-shadow: 0 0 0 1px #f5c2c7 inset !important;
}
.mini-pill {
	display: inline-flex;
	align-items: center;
	background: #f7fafd;
	border: 1px solid #e3e8ee;
	border-radius: 12px;
	padding: 0.07rem 0.38rem;
	font-size: 0.68em;
	min-height: 16px;
	gap: 0.18rem;
	color: $color_2;
	transition: background 0.18s, border-color 0.18s, color 0.18s;
	margin-bottom: 1px;
	font-weight: 500;
}
.mini-pill.is-active {
	background: #e3f0fa;
	border-color: $border-color_1;
	color: $color_3;
}
.mini-pill.pill-disabled {
	background: #f8d7da;
	color: $color_1;
	border: 1px solid #f5c2c7;
	cursor: not-allowed;
}
.mini-pill-btn {
	all: unset;
	cursor: pointer;
	display: flex;
	align-items: center;
	min-width: 0;
	width: max-content;
}
.mini-pill-label {
	font-size: 1em;
	font-weight: 500;
	letter-spacing: 0.01em;
	line-height: 1.1;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 70px;
}
.mini-pill-number {
	font-size: 0.82em;
	margin-left: 0.15rem;
	color: $color_4;
}
.mini-pill-remove {
	all: unset;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 14px;
	height: 14px;
	border-radius: 3px;
	color: $color_5;
	margin-left: 0.08rem;
	font-size: 1em;
	transition: background 0.13s, color 0.13s;
	&:hover {
		background: #e0e6ed;
		color: $color_3;
	}
	&:focus-visible {
		outline: 2px solid #1790da;
		outline-offset: 2px;
	}
}
.locked-block {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 120px;
	background: #f8d7da;
	border: 1px solid #f5c2c7;
	border-radius: 1rem;
	margin-bottom: 2rem;
	cursor: not-allowed;
}
.locked-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 1rem;
}
.ocr-upload-card {
	margin-bottom: 0.75rem;
	background: #f8fafc;
	border: 1px solid #dbe7ef;
	border-radius: 10px;
	padding: 0.6rem 0.75rem;
}
.ocr-upload-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	flex-wrap: wrap;
	h6 {
		margin: 0;
		font-size: 0.8rem;
		color: $color_6;
	}
}
.ocr-upload-input {
	display: none;
}
.ocr-upload-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: 1px solid #cbd5e1;
	background: #fff;
	color: $color_6;
	border-radius: 999px;
	font-size: 0.72rem;
	font-weight: 600;
	padding: 0.24rem 0.72rem;
	cursor: pointer;
}
.ocr-status {
	margin-top: 0.45rem;
	display: flex;
	align-items: center;
	gap: 0.45rem;
	font-size: 0.72rem;
	color: $color_6;
}
.ocr-hint {
	margin: 0.45rem 0 0;
	font-size: 0.7rem;
	color: $color_7;
}
.ocr-error {
	margin-top: 0.45rem;
}
.locked-text {
	color: $color_1;
	font-weight: 600;
	text-align: center;
	font-size: 1.1rem;
}
.form-grid-lead-matching {
    display: grid;
    grid-template-columns: 3fr 1fr;
    gap: .55rem .6rem;
}

.form-grid-lead-matching {
	.full-width {
		grid-column: 1 / 3;
		grid-column: 1 / 3;
	}
	.street {
		grid-column: 1 / 2;
		grid-column: 1 / 2;
		.login-input {
			width: 100%;
		}
	}
	.house-number {
		grid-column: 2 / 3;
		min-width: 0;
		grid-column: 2 / 3;
		.login-input {
			width: 100%;
		}
	}
	.postal-code {
		grid-column: 1 / 2;
		min-width: 0;
		grid-column: 1 / 2;
		.login-input {
			width: 100%;
		}
	}
	.city {
		grid-column: 2 / 3;
		grid-column: 2 / 3;
		.login-input {
			width: 100%;
		}
	}
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: .55rem .6rem;
}
.form-sub {
	.required {
		color: $color_8;
		font-weight: 600;
		font-size: .95em;
		margin-left: .3em;
	}
	margin: 0 0 .75rem;
	font-size: .65rem;
	opacity: .7;
}
.sr-only {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0 0 0 0);
	border: 0;
}
.search-block {
    .form-errors {
        flex-direction: row;
        align-items: center;
    }
	.search-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: .25rem;
		padding: .45rem .6rem;
		line-height: 1;
		img {
			width: .85rem;
			height: .85rem;
			filter: brightness(0) invert(1);
			opacity: .9;
			transition: .25s opacity,.25s filter;
		}
		&:hover {
			&:not(:disabled) {
				img {
					opacity: 1;
				}
				background: rgba(255,255,255,0.07);
			}
		}
		&:disabled {
			img {
				opacity: .35;
			}
		}
		&:focus-visible {
			outline: 2px solid var(--sellwerk-color-secondary,#1790da);
			outline-offset: 2px;
		}
	}
}
.theme--light {
	.search-block {
		.search-btn {
			img {
				filter: none;
				opacity: .85;
			}
			&:hover {
				&:not(:disabled) {
					background: #eef3f7;
					box-shadow: 0 0 0 1px #d3dbe3 inset;
					img {
						opacity: 1;
						filter: brightness(0) saturate(100%) contrast(140%);
					}
				}
			}
			&:active {
				&:not(:disabled) {
					background: #e2e9ee;
				}
			}
		}
	}
	.leadListEmpty {
		p {
			color: $color_12;
		}
	}
	.empty-retry-btn.btn.btn--solid {
		background: var(--sellwerk-color-main, #00a393);
		border-color: $border-color_4;
		color: $color_10;
		&:hover {
			&:not(:disabled) {
				background: var(--sellwerk-color-tertiary, #28817e);
				border-color: $border-color_2;
				color: $color_10;
			}
		}
	}
	.capacity-inline {
		color: $color_17;
		.cap-pill {
			background: #e6eef3;
			color: $color_18;
			box-shadow: 0 0 0 1px #ccd7de inset;
		}
	}
	.capacity-inline.at-limit {
		.cap-pill {
			background: #f8e8d2;
			color: $color_19;
			box-shadow: 0 0 0 1px #d9b075 inset;
		}
	}
	.rv-list {
		li {
			background: #ffffff;
			border-color: $border-color_12;
			&:hover {
				background: #f1f6fa;
				.rs-remove {
					color: $color_6;
				}
			}
		}
		.rs-remove {
			color: $color_12;
			&:hover {
				background: #e2eaf0;
				color: $color_27;
			}
		}
	}
	.rv-remove {
		opacity: .55;
		&:hover {
			opacity: .9;
		}
	}
	.tabs-switch {
		background: #f1f5f9;
		border-color: $border-color_11;
	}
	.tab-btn {
		color: $color_7;
		&:not(.active) {
			&:hover {
				background: #e6eef3;
				color: $color_18;
			}
		}
	}
	.tab-btn.active {
		background: #ffffff;
		color: $color_18;
		box-shadow: 0 1px 2px rgba(0,0,0,.06);
	}
	.rs-clear {
		background: #f1f5f9;
		border: 1px solid #d3dbe3;
		color: $color_7;
		&:hover {
			background: #e6eef3;
			color: $color_18;
		}
	}
	.recent-searches {
		li {
			background: #ffffff;
			border-color: $border-color_12;
			&:hover {
				background: #f1f6fa;
				.rs-remove {
					color: $color_6;
				}
			}
		}
		.rs-remove {
			color: $color_12;
			&:hover {
				background: #e2eaf0;
				color: $color_27;
			}
		}
	}
	.form-card {
		background: #fff;
	}
	.login-field {
		background: #f8fafc;
		border-color: $border-color_13;
	}
	.login-input {
		color: $color_18;
	}
	.form-errors {
		background: #fff1f2;
		border-color: $border-color_14;
		color: $color_29;
	}
	.matching-loading-overlay {
		background: rgba(255, 255, 255, .82);
		color: $color_18;
	}
}
.search-block.below {
	display: flex;
	align-items: center;
	gap: .55rem;
	flex: 1 1 auto;
	width: 100%;
	justify-content: flex-start;
	.search-input {
		flex: 1 1 auto;
		width: 100%;
		min-width: 0;
	}
}
.leadList.dummy {
	opacity: .55;
	h6 {
		font-weight: 500;
	}
}
.leadList.is-loading {
	cursor: progress;
}
.leadList.is-empty {
	cursor: default;
}
.leadListEmpty {
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: .2rem;
	min-height: 2.1rem;
	padding-right: 1.7rem;
	h6 {
		margin: 0;
	}
	p {
		margin: 0;
		font-size: .56rem;
		color: $color_9;
		letter-spacing: .2px;
	}
}
.empty-retry-btn {
	align-self: flex-start;
	margin-top: .1rem;
	padding: .24rem .55rem;
	font-size: .54rem;
	line-height: 1;
}
.empty-retry-btn.btn.btn--solid {
	background: var(--sellwerk-color-main, #00a393);
	border: 1px solid var(--sellwerk-color-main, #00a393);
	color: $color_10;
	box-shadow: 0 1px 2px rgba(0, 0, 0, .22);
	&:hover {
		&:not(:disabled) {
			background: var(--sellwerk-color-tertiary, #28817e);
			border-color: $border-color_2;
			color: $color_10;
		}
	}
	&:active {
		&:not(:disabled) {
			background: var(--sellwerk-color-tertiary, #28817e);
			border-color: $border-color_2;
			filter: brightness(.92);
		}
	}
	&:focus-visible {
		outline: 2px solid var(--sellwerk-color-secondary, #97d2d4);
		outline-offset: 2px;
	}
	&:disabled {
		background: var(--sellwerk-color-secondary, #97d2d4);
		border-color: $border-color_3;
		color: $color_11;
		opacity: 1;
	}
}
.capacity-inline {
	display: flex;
	align-items: center;
	gap: .6rem;
	margin: 0 0 .45rem;
	font-size: .62rem;
	font-weight: 500;
	letter-spacing: .4px;
	color: $color_13;
	.cap-label {
		text-transform: uppercase;
		font-weight: 600;
		opacity: .6;
		font-size: .58rem;
		letter-spacing: .55px;
	}
	.cap-counter-pills {
		display: flex;
		gap: 0.25rem;
	}
	.cap-pill {
		display: flex;
		align-items: center;
		font-weight: 600;
		background: #1d2730;
		color: $color_15;
		padding: .2rem .52rem .22rem;
		border-radius: 999px;
		font-size: .62rem;
		letter-spacing: .5px;
		box-shadow: 0 0 0 1px #26323c inset;
		min-width: 2.2em;
		text-align: center;
	}
	.cap-msg {
		font-size: .6rem;
		font-weight: 600;
		letter-spacing: .45px;
		margin-left: 0.5em;
	}
}
.capacity-inline.at-limit {
	color: $color_14;
	.cap-pill {
		background: #452c11;
		color: $color_16;
		box-shadow: 0 0 0 1px #8a5a14 inset;
	}
}
.latest-searches {
	list-style: none;
	padding: 0;
	margin: .4rem 0 0;
	display: flex;
	flex-direction: column;
	gap: .5rem;
	li {
		cursor: pointer;
		font-size: .55rem;
		padding: .35rem .6rem;
		background: #fff;
		border: 1px solid #e2e8f0;
		border-radius: 8px;
		letter-spacing: .04em;
		&:hover {
			background: #f1f5f9;
		}
	}
}
.theme-dark {
	.latest-searches {
		li {
			background: #1e293b;
			border-color: $border-color_5;
			color: $color_20;
			&:hover {
				background: #334155;
			}
		}
	}
}
.theme--dark {
	.latest-searches {
		li {
			background: #1e293b;
			border-color: $border-color_5;
			color: $color_20;
			&:hover {
				background: #334155;
			}
		}
	}
}
.recently-viewed {
	margin-top: .6rem;
}
.rv-title {
	margin: 0 0 .35rem;
	font-size: .55rem;
	font-weight: 600;
	letter-spacing: .45px;
	opacity: .75;
	letter-spacing: .5px;
	opacity: .7;
}
// .rv-list {
// 		li {
// 			background: #ffffff;
// 			border-color: $border-color_12;
// 			&:hover {
// 				background: #f1f6fa;
// 				.rs-remove {
// 					color: $color_6;
// 				}
// 			}
// 		}
// 		.rs-remove {
// 			color: $color_12;
// 			&:hover {
// 				background: #e2eaf0;
// 				color: $color_27;
// 			}
// 		}
// 	}
.rv-remove {
	background: transparent;
	border: 0;
	color: $color_21;
	cursor: pointer;
	font-size: .7rem;
	line-height: 1;
	padding: 0 .15rem;
	opacity: .5;
	border-radius: 4px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	&:hover {
		opacity: .9;
	}
	&:focus-visible {
		outline: 1px solid currentColor;
		outline-offset: 2px;
	}
}
.rv-empty {
	font-size: .55rem;
	opacity: .55;
	margin: .15rem 0 0;
}
.filter-inline {
	margin-bottom: .45rem;
}
.recent-searches-wrapper {
	margin-top: .55rem;
	display: flex;
	flex-direction: column;
	gap: .4rem;
	width: max-content;
	max-width: 100%;
	.rs-headline {
		margin: 0;
		font-size: .55rem;
		letter-spacing: .45px;
		font-weight: 600;
		opacity: .75;
		text-transform: uppercase;
	}
}
.tabs-switch {
	display: inline-flex;
	gap: .35rem;
	background: #141b22;
	padding: .25rem;
	border: 1px solid #25313b;
	border-radius: 10px;
	margin: 0 0 .4rem;
}
.tab-btn {
	background: transparent;
	border: 0;
	font-size: .55rem;
	font-weight: 600;
	letter-spacing: .4px;
	padding: .4rem .75rem;
	border-radius: 8px;
	cursor: pointer;
	color: $color_22;
	line-height: 1;
	display: inline-flex;
	align-items: center;
	gap: .35rem;
	transition: .25s background,.25s color;
	&:not(.active) {
		&:hover {
			background: #1b242c;
			color: $color_23;
		}
	}
}
.tab-btn.active {
	background: #1d2730;
	color: $color_20;
}
.tab-panel {
	animation: fadeIn .25s ease;
}
.rs-headline-row {
	display: flex;
	align-items: center;
	gap: .75rem;
}
.rs-clear {
	all: unset;
	cursor: pointer;
	font-size: .48rem;
	letter-spacing: .4px;
	font-weight: 500;
	color: $color_24;
	padding: .25rem .55rem;
	background: #202830;
	border: 1px solid #2d3842;
	border-radius: 999px;
	line-height: 1;
	transition: .2s background,.2s color,.2s border-color;
	&:hover {
		background: #27323c;
		color: $color_25;
	}
	&:focus-visible {
		outline: 2px solid var(--sellwerk-color-secondary,#1790da);
		outline-offset: 2px;
	}
}
.recent-searches,
.rv-list {
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-wrap: wrap;
	gap: .35rem;
	li {
		position: relative;
		display: flex;
		align-items: center;
		background: #1d232a;
		border: 1px solid #2c353f;
		border-radius: 999px;
		padding: 0 .55rem;
		gap: .45rem;
		min-height: 26px;
		max-width: 100%;
		&:hover {
			background: #24303a;
			.rs-remove {
				color: $color_23;
			}
		}
	}
	.rs-item-btn {
		all: unset;
		cursor: pointer;
		display: flex;
		align-items: center;
		min-width: 0;
		width: max-content;
	}
	.lbl {
		font-size: .55rem;
		font-weight: 500;
		letter-spacing: .3px;
		line-height: 1.2;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.rs-remove {
		all: unset;
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		border-radius: 6px;
		color: $color_26;
		transition: .2s background,.2s color;
		&:hover {
			background: #2f3a44;
			color: $color_16;
		}
		&:focus-visible {
			outline: 2px solid var(--sellwerk-color-secondary,#1790da);
			outline-offset: 2px;
		}
	}
}
.form-title {
	margin: 0 0 .35rem;
	font-size: .9rem;
	font-weight: 700;
	letter-spacing: .4px;
}
.grid.two-cols {
	display: grid;
	grid-template-columns: repeat(2, minmax(0,1fr));
	gap: .55rem .6rem;
}
fieldset {
	border: 0;
	margin: 0;
	padding: 0;
}
legend {
	padding: 0;
}
.login-field {
	display: flex;
	align-items: center;
	gap: .45rem;
	padding: .4rem .5rem;
	border: 1px solid #2c3742;
	border-radius: 10px;
	background: #0f1620;
	img {
		width: .85rem;
		height: .85rem;
		opacity: .85;
	}
}
.login-input {
	flex: 1 1 auto;
	background: transparent;
	border: 0;
	color: $color_20;
	font-size: .85rem;
	width: 100%;
	&::placeholder {
		color: $color_22;
	}
	&:focus-visible {
		outline: 2px solid var(--sellwerk-color-secondary,#1790da);
		outline-offset: 2px;
		border-radius: 6px;
	}
}
.form-errors {
	margin-top: 12px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: center;
	gap: 6px;
	width: 100%;
	text-align: center;
	padding: .5rem .6rem;
	border-radius: 10px;
	border: 1px solid #8b1f1f;
	background: #2a1717;
	color: $color_28;
	font-size: .7rem;
	list-style: disc;
    &.search-loading {
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: .45rem;
    }
	li {
		margin-left: 1rem;
	}
}

.actions {
	margin-top: .75rem;
	display: flex;
	justify-content: flex-end;
	.btn.btn--solid {
		padding: .55rem .9rem;
		border-radius: 10px;
		background: var(--sellwerk-color-main,#007d72);
		border: 1px solid var(--sellwerk-color-main,#007d72);
		color: $color_16;
		font-weight: 700;
		letter-spacing: .3px;
		cursor: pointer;
		box-shadow: 0 1px 1px rgba(0,0,0,.18);
		&:hover {
			&:not(:disabled) {
				background: var(--sellwerk-color-tertiary,#05635c);
				border-color: $border-color_15;
			}
		}
		&:disabled {
			opacity: .62;
			filter: saturate(.55);
			cursor: not-allowed;
		}
	}
}
.matching-form-container {
	position: relative;
}
.matching-loading-overlay {
	position: absolute;
	inset: 0;
	z-index: 10;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: .45rem;
	border-radius: inherit;
	background: rgba(10, 20, 30, .72);
	font-size: .72rem;
	font-weight: 600;
	color: $color_20;
	pointer-events: all;
	border-radius: 10px;
}
@media (min-width: 1380px) {
	.mini-pill-label {
		max-width: 110px;
	}
	.rv-list {
		li {
			.lbl.ellipsis {
				max-width: 140px;
			}
		}
	}
}
@media (max-width: 720px) {
	.form-grid-lead-matching {
		grid-template-columns: 1fr;
		grid-template-columns: 1fr;
		.full-width {
			grid-column: 1 / 2;
			grid-column: 1 / 2;
		}
		.street {
			grid-column: 1 / 2;
			grid-column: 1 / 2;
		}
		.house-number {
			grid-column: 1 / 2;
			grid-column: 1 / 2;
		}
		.postal-code {
			grid-column: 1 / 2;
			grid-column: 1 / 2;
		}
		.city {
			grid-column: 1 / 2;
			grid-column: 1 / 2;
		}
	}
	.grid.two-cols {
		grid-template-columns: 1fr;
	}
}
</style>